import * as R from 'ramda';
import { AppsPermissionSelector } from 'services/marketplace/modules/AppsPermissionsModule';
import features, { FeatureName } from 'system/features';
import router from 'system/router';
import { reduxHelper } from 'utils/reduxHelper';

import { GlobalState } from 'reactApp/core/rootReducer';
import { BillingPermissionsSelectors } from 'reactApp/modules/PermissionsModule/BillingPermissionsModule';
import * as PermissionTypes from 'reactApp/modules/PermissionsModule/PermissionModule.types';
import {
    INFRA_SERVICES,
    MCS_SERVICES,
    PermissionSelectors,
} from 'reactApp/modules/PermissionsModule/PermissionsModule';
import { ProjectSelectors } from 'reactApp/modules/ProjectModule';
import * as ServiceTypes from 'reactApp/modules/ServiceModule/ServiceModule.types';
import { WhiteLabelSelectors } from 'reactApp/modules/WhiteLabelModule/WhiteLabelModule';

const _getIsServiceAvailable = (
    serviceName: ServiceTypes.InternalServiceName,
) => R.partialRight(WhiteLabelSelectors.getIsServiceAvailable, [serviceName]);

const _getIsFeatureEnabled = (name: FeatureName) => () =>
    features.isEnabled(name);

type PageParams = {
    featureName?: FeatureName;
    view?: PermissionTypes.PermissionName[];
    otherPreds?: R.Pred[];
};

type ServiceParams = PageParams & {
    serviceInternalName?: ServiceTypes.InternalServiceName;
};
const SERVICES_PARAMS: { [key: string]: ServiceParams } = {
    '#infra-servers': {
        view: [MCS_SERVICES.infra, INFRA_SERVICES.nova],
        serviceInternalName: 'infra',
    },
    '#infra-firewall': {
        view: [INFRA_SERVICES.neutron, INFRA_SERVICES.nova],
        serviceInternalName: 'infra',
    },
    '#backups': {
        view: [MCS_SERVICES.infra, INFRA_SERVICES.nova, INFRA_SERVICES.karbor],
        featureName: 'KARBOR_BACKUPS',
        serviceInternalName: 'infra',
    },
    '#balancers': {
        view: [INFRA_SERVICES.neutron],
        featureName: 'LOAD_BALANCER',
        serviceInternalName: 'infra',
    },
    '#routers': {
        view: [INFRA_SERVICES.neutron],
        serviceInternalName: 'infra',
    },
    '#networks': {
        view: [INFRA_SERVICES.neutron, INFRA_SERVICES.neutron],
        featureName: 'NETWORKS',
        serviceInternalName: 'infra',
    },
    '#vpn-tunnels': {
        view: [INFRA_SERVICES.neutron],
        featureName: 'VPN_TUNNELS',
        serviceInternalName: 'infra',
    },
    '#special-services-main': {
        featureName: 'SPECIAL_SERVICES',
    },
    '#monitoring-main': {
        featureName: 'MONITORING',
    },
    '#volumes': {
        view: [MCS_SERVICES.infra, INFRA_SERVICES.nova, INFRA_SERVICES.cinder],
        featureName: 'VOLUMES',
        serviceInternalName: 'infra',
    },
    '#images': {
        view: [INFRA_SERVICES.glance],
    },
    '#floating-ips': {
        view: [INFRA_SERVICES.neutron],
    },
    '#key-pairs': {
        view: [MCS_SERVICES.infra, INFRA_SERVICES.nova],
    },
    '#shares-list': {
        view: [MCS_SERVICES.infra, INFRA_SERVICES.manila],
        featureName: 'MANILA',
        serviceInternalName: 'infra',
    },

    '#marketplace-main': {
        serviceInternalName: 'marketplace',
        featureName: 'MARKETPLACE_SERVICE',
        view: [MCS_SERVICES.marketplace],
    },

    '#app-wizard': {
        serviceInternalName: 'marketplace',
        featureName: 'MARKETPLACE_SERVICE',
        otherPreds: [AppsPermissionSelector.createApp],
    },

    '#storage-main': {
        serviceInternalName: 'storage',
        featureName: 'CLOUD_STORAGE_SERVICE',
        view: [MCS_SERVICES.hotbox],
    },

    '#containers-main': {
        serviceInternalName: 'containers',
        featureName: 'CLOUD_CONTAINERS_SERVICE',
        view: [MCS_SERVICES.containers],
    },

    '#databases-main': {
        serviceInternalName: 'databases',
        featureName: 'CLOUD_DATABASES_SERVICE',
        view: [MCS_SERVICES.databases],
    },

    // todo: сделать проверку сервиса когда будет
    '#analytic-databases-main': {
        // serviceInternalName: 'analyticDatabases',
        featureName: 'ANALYTIC_DATABASES_SERVICE',
        view: [MCS_SERVICES.bigdata],
    },

    '#bigdata-main': {
        serviceInternalName: 'bigdata',
        featureName: 'CLOUD_BIG_DATA_SERVICE',
        view: [MCS_SERVICES.bigdata],
    },

    '#gpu-main': {
        serviceInternalName: 'gpu',
        featureName: 'CLOUD_GPU_SERVICE',
        view: [MCS_SERVICES.gpu],
    },

    '#machine-learning-main': {
        serviceInternalName: 'machineLearning',
        featureName: 'MACHINE_LEARNING_SERVICE',
        view: [MCS_SERVICES.vision],
    },
};

const OTHER_PAGES_PARAMS: { [key: string]: PageParams } = {
    '#access': {
        view: [MCS_SERVICES.accounts],
        otherPreds: [WhiteLabelSelectors.getAccessConstrolEnabled],
    },

    '#billing-main': {
        otherPreds: [BillingPermissionsSelectors.getCanViewService],
    },
    '#billing-pay': {
        otherPreds: [BillingPermissionsSelectors.getCanViewService],
    },
    '#billing-bind-card': {
        otherPreds: [BillingPermissionsSelectors.getCanViewService],
    },

    '#partner-clients': {
        otherPreds: [ProjectSelectors.getIsPartnerApproved],
    },

    '#main': {
        otherPreds: [getIsMainPageAvailable],
    },
    '#network-add': {
        otherPreds: [WhiteLabelSelectors.getIsNetworkCreationAvailable],
    },
};

const makeGetter = (params: ServiceParams) => {
    const { view = [], otherPreds = [] } = params;

    return R.allPass(
        [
            PermissionSelectors.checkPermissions({
                view,
            }),
            R.allPass(otherPreds),
            params.featureName && _getIsFeatureEnabled(params.featureName),
            params.serviceInternalName &&
                _getIsServiceAvailable(params.serviceInternalName),
        ].filter(R.identity) as R.Pred[],
    );
};

const SERVICE_PERMISSION_GETTERS = R.mapObjIndexed(makeGetter, SERVICES_PARAMS);

const infraMainPermissionGetter = R.anyPass([
    // can activate service
    PermissionSelectors.checkPermissions({
        view: [MCS_SERVICES.infra],
    }),

    // or have subitems
    ...[
        '#infra-servers',
        '#infra-firewall',
        '#backups',
        '#balancers',
        '#routers',
        '#networks',
        '#volumes',
        '#images',
        '#shares-list',
    ].map((routeId) => SERVICE_PERMISSION_GETTERS[routeId]),
]);

SERVICE_PERMISSION_GETTERS['#infra-main'] = infraMainPermissionGetter;

const OTHER_PAGES_PERMISSIONS_GETTERS = R.mapObjIndexed(
    makeGetter,
    OTHER_PAGES_PARAMS,
);

const PERMISSIONS_GETTERS: typeof SERVICE_PERMISSION_GETTERS = {
    '#index': R.T,

    ...SERVICE_PERMISSION_GETTERS,

    ...OTHER_PAGES_PERMISSIONS_GETTERS,
};

const getIsRoutePermitted = R.curry((state: GlobalState, routeId: string) => {
    if (['#__root__', '#app', '#partner-app', '#services'].includes(routeId)) {
        return true;
    }
    const permissionGetter = PERMISSIONS_GETTERS[routeId];
    if (permissionGetter) {
        return permissionGetter(state);
    }
    const route = router.getRoute(routeId);
    if (!route || !route.parentId) {
        return true;
    }
    return getIsRoutePermitted(state, route.parentId);
});

function getIsMainPageAvailable(state) {
    const getIsRoutePermittedCurried = getIsRoutePermitted(state);

    const routes = [
        '#infra-main',
        '#storage-main',
        '#containers-main',
        '#databases-main',
    ];

    return Boolean(routes.filter(getIsRoutePermittedCurried).length);
}

export const RoutePermissionsSelector = {
    getIsRoutePermitted,
};

export const BoundRoutePermissionsSelectors = reduxHelper.bindSelectors<
    typeof RoutePermissionsSelector
>(RoutePermissionsSelector);
